// Voice and video calls (phase 6).
//
// Ringing uses Jingle Message Initiation (XEP-0353): <propose/> goes to the
// callee's bare JID so all their devices ring; they answer with <ringing/>,
// <proceed/> or <reject/>; the caller can <retract/>; either side <finish/>es.
// Once a device accepts, the WebRTC offer/answer travels end-to-end encrypted
// (Signal session, to that one device only), so the server can't swap the
// DTLS fingerprints and listen in. Media is peer-to-peer (DTLS-SRTP); when
// that's impossible it's relayed by ejabberd's TURN server using short-lived
// credentials from XEP-0215.
//
// Group calls are a full mesh (everyone connects to everyone, up to
// MAX_GROUP), so they stay end-to-end encrypted too. Invite/join/leave go
// through the group; the SDP goes 1:1 as above.
//
// History: when a 1:1 call ends the caller's device posts a <call-log/>
// message (stored in MAM, copied to every device), so all devices of both
// people show the same "Missed voice call" entry. A group call's history is
// its invite message, updated by <call-end/>.
import { signal } from "@preact/signals";
import { xml, type Element } from "@xmpp/client";
import * as S from "./state";
import { isNative } from "./platform";
import { bare, uuid } from "./util";

/** A phone or tablet (its camera turns with the screen). */
const handheld = () => isNative || (matchMedia("(pointer: coarse)").matches && /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent));

/** Camera constraints that match how the device is held. Asking a phone held
 * upright (a portrait camera) for 1280x720 makes some Android cameras and
 * WebViews squeeze the picture into a landscape frame, so the other side sees
 * it stretched; a portrait request, cropped rather than scaled, keeps it
 * undistorted. Desktops keep 1280x720. */
function cameraConstraints(facing: "user" | "environment"): MediaTrackConstraints {
  const portrait = handheld() && matchMedia("(orientation: portrait)").matches;
  const [width, height] = portrait ? [720, 1280] : [1280, 720];
  return { facingMode: facing, width: { ideal: width }, height: { ideal: height }, aspectRatio: { ideal: width / height }, resizeMode: { ideal: "crop-and-scale" } } as MediaTrackConstraints;
}

export const NS_JMI = "urn:xmpp:jingle-message:0";
export const NS_RTP = "urn:xmpp:jingle:apps:rtp:1";
export const NS_CALL = "urn:xmm:call:0";
const NS_EXTDISCO = "urn:xmpp:extdisco:2";
const NS_HINTS = "urn:xmpp:hints";
const NS_CARBONS = "urn:xmpp:carbons:2";

export type CallMedia = "audio" | "video";
export type CallResult = "answered" | "missed" | "declined" | "busy" | "failed" | "ended";

export const RING_MS = 45_000; // unanswered calls stop ringing after this
export const MAX_GROUP = 8; // mesh: every participant sends to every other

export interface PeerView {
  key: string; // full JID of the peer's device
  jid: string;
  stream: MediaStream;
  state: "connecting" | "connected" | "reconnecting";
  muted: boolean;
  video: boolean;
  screen: boolean;
}

export interface CallView {
  sid: string;
  chat: string; // contact or group JID
  group: boolean;
  media: CallMedia;
  dir: "in" | "out";
  /** incoming: ringing here; outgoing: calling / ringing there; connecting:
   * accepted, setting up media; active: talking; ended: brief end screen */
  phase: "incoming" | "outgoing" | "ringing" | "connecting" | "active" | "ended";
  caller: string;
  startedAt?: number;
  ended?: string;
  local: MediaStream | null; // what we send (preview)
  muted: boolean;
  camera: boolean;
  screen: boolean;
  canFlip: boolean;
  peers: PeerView[];
}

export interface GroupCallInfo {
  sid: string;
  media: CallMedia;
  startedBy: string;
  since: number;
  members: Map<string, { full: string; device: number }>; // full JID -> device
}

/** The call on this device (one at a time). */
export const call = signal<CallView | null>(null);
/** Group calls known to be going on right now, by group JID. */
export const groupCalls = signal(new Map<string, GroupCallInfo>());

export interface CallHost {
  me: string;
  full: string;
  deviceId(): number;
  online(): boolean;
  send(el: Element): Promise<unknown>;
  iq(el: Element): Promise<Element>;
  /** <encrypted op="call"/> for one device */
  encryptFor(jid: string, deviceId: number, payload: object): Promise<Element>;
  decrypt(from: string, el: Element): Promise<unknown>;
  logCall(chat: string, sid: string, media: CallMedia, result: CallResult, duration: number): Promise<void>;
}

interface Link {
  key: string;
  jid: string;
  device: number;
  pc: RTCPeerConnection;
  ctl: RTCDataChannel;
  view: PeerView;
  offerer: boolean;
  restarts: number;
  lost?: number;
  /** trickle ICE: candidates found after our SDP went out are sent in
   * batches; remote ones that arrive before their SDP wait in `inCands` */
  sdpSent: boolean;
  outCands: RTCIceCandidateInit[];
  inCands: RTCIceCandidateInit[];
  flush?: number;
  chain: Promise<unknown>; // keeps our signals in order
}

interface Current {
  sid: string;
  chat: string;
  group: boolean;
  media: CallMedia;
  dir: "in" | "out";
  caller: string;
  callerFull?: string; // 1:1 incoming: the device that called
  callerDevice?: number;
  links: Map<string, Link>;
  audio: MediaStreamTrack | null;
  camera: MediaStreamTrack | null;
  screen: MediaStreamTrack | null;
  facing: "user" | "environment";
  connectedAt?: number;
  joined: boolean; // group: we're in it
  timer?: number;
  done?: boolean;
}

interface SignalPayload {
  kind?: string;
  sdp?: string;
  device?: number;
  cands?: RTCIceCandidateInit[];
}

const hint = (name: "store" | "no-store" | "no-copy") => xml(name, { xmlns: NS_HINTS });

export class CallManager {
  private cur: Current | null = null;
  private silenced = new Set<string>(); // call ids we silenced (unknown callers)
  private ice: { servers: RTCIceServer[]; until: number } | null = null;
  readonly tones = new Tones();

  private orientation = matchMedia("(orientation: portrait)");
  /** The phone turned during a video call: follow with the camera's shape. */
  private onTurn = () => {
    const cam = this.cur?.camera;
    if (cam && cam.readyState === "live" && handheld()) void cam.applyConstraints(cameraConstraints(this.cur!.facing)).catch((e) => console.warn("camera orientation", e));
  };

  constructor(private host: CallHost) {
    this.orientation.addEventListener("change", this.onTurn);
  }

  get busy() {
    return !!this.cur;
  }

  // ---- public API (UI) ---------------------------------------------------------

  async start(chat: string, media: CallMedia) {
    if (this.cur) throw new Error("You're already in a call");
    if (!this.host.online()) throw new Error("You're offline");
    const group = S.isGroup(chat);
    if (group) {
      const g = groupCalls.value.get(chat);
      if (g) return this.joinGroup(chat, g.sid); // join the ongoing one instead
    }
    const sid = uuid();
    const cur = this.newCurrent(sid, chat, group, media, "out", this.host.me);
    this.cur = cur;
    this.publish({ phase: "outgoing" });
    try {
      await this.acquire(cur, media === "video");
    } catch (e) {
      this.cur = null;
      call.value = null;
      throw e;
    }
    if (this.cur !== cur) return;
    this.publish({});
    if (group) {
      cur.joined = true;
      await this.host.send(
        xml(
          "message",
          { to: chat, type: "groupchat", id: sid },
          xml("call-invite", { xmlns: NS_CALL, id: sid, media, device: String(this.host.deviceId()), resource: this.resource() }),
          xml("body", {}, media === "video" ? "📹 Group video call" : "📞 Group voice call"),
          xml("origin-id", { xmlns: "urn:xmpp:sid:0", id: sid }),
          hint("store"),
        ),
      );
      this.trackGroup(chat, { sid, media, startedBy: this.host.me, since: Date.now(), members: new Map() }, this.host.full, this.host.deviceId());
      cur.timer = window.setTimeout(() => {
        if (this.cur === cur && !cur.links.size) void this.hangup("No one joined");
      }, RING_MS + 15_000);
    } else {
      await this.host.send(
        xml(
          "message",
          { to: chat, type: "chat", id: uuid() },
          xml(
            "propose",
            { xmlns: NS_JMI, id: sid },
            xml("description", { xmlns: NS_RTP, media: "audio" }),
            media === "video" ? xml("description", { xmlns: NS_RTP, media: "video" }) : null,
            xml("device", { xmlns: NS_CALL, id: String(this.host.deviceId()) }),
          ),
          // A body makes ejabberd push to offline devices; the backend turns
          // exactly this text into an "incoming call" notification.
          xml("body", {}, media === "video" ? "📹 Incoming video call" : "📞 Incoming voice call"),
          hint("store"),
        ),
      );
      cur.timer = window.setTimeout(() => {
        if (this.cur === cur && (call.value?.phase === "outgoing" || call.value?.phase === "ringing")) void this.hangup("No answer");
      }, RING_MS);
    }
    this.tones.ringback();
  }

  async accept(asVideo?: boolean) {
    const cur = this.cur;
    if (!cur || call.value?.phase !== "incoming") return;
    this.tones.stop();
    clearTimeout(cur.timer);
    this.publish({ phase: "connecting" });
    try {
      await this.acquire(cur, asVideo ?? cur.media === "video");
    } catch (e) {
      if (!cur.group)
        await this.host.send(xml("message", { to: cur.chat, type: "chat", id: uuid() }, xml("reject", { xmlns: NS_JMI, id: cur.sid }), hint("no-store")));
      this.teardown(null);
      throw e;
    }
    if (this.cur !== cur) return;
    this.publish({});
    if (cur.group) return this.enterGroup(cur);
    await this.host.send(
      xml(
        "message",
        { to: cur.callerFull!, type: "chat", id: uuid() },
        xml("proceed", { xmlns: NS_JMI, id: cur.sid }, xml("device", { xmlns: NS_CALL, id: String(this.host.deviceId()) })),
        hint("no-store"),
      ),
    );
    this.newLink(cur, cur.callerFull!, cur.callerDevice!, false);
    // The caller sends its offer right away; give up if nothing comes.
    cur.timer = window.setTimeout(() => {
      if (this.cur === cur && !cur.connectedAt) void this.hangup("Couldn't connect");
    }, 30_000);
  }

  async decline() {
    const cur = this.cur;
    if (!cur || call.value?.phase !== "incoming") return;
    if (!cur.group)
      await this.host.send(xml("message", { to: cur.chat, type: "chat", id: uuid() }, xml("reject", { xmlns: NS_JMI, id: cur.sid }), hint("no-store")));
    this.teardown(null);
  }

  /** Leave / end the call (or cancel it while it's ringing). */
  async hangup(reason = "Call ended") {
    const cur = this.cur;
    if (!cur || cur.done) return;
    const phase = call.value?.phase;
    if (phase === "incoming") return this.decline();
    const duration = cur.connectedAt ? Math.round((Date.now() - cur.connectedAt) / 1000) : 0;
    if (cur.group) {
      if (cur.joined) await this.leaveGroup(cur, duration);
    } else if (phase === "outgoing" || phase === "ringing") {
      await this.host.send(xml("message", { to: cur.chat, type: "chat", id: uuid() }, xml("retract", { xmlns: NS_JMI, id: cur.sid }), hint("store")));
      await this.host.logCall(cur.chat, cur.sid, cur.media, "missed", 0);
    } else {
      for (const l of cur.links.values())
        await this.host.send(
          xml("message", { to: l.key, type: "chat", id: uuid() }, xml("finish", { xmlns: NS_JMI, id: cur.sid }, xml("reason", {}, xml(cur.connectedAt ? "success" : "connectivity-error"))), hint("no-store")),
        );
      if (cur.dir === "out") await this.host.logCall(cur.chat, cur.sid, cur.media, cur.connectedAt ? "answered" : "failed", duration);
    }
    this.teardown(reason);
  }

  toggleMute() {
    const cur = this.cur;
    if (!cur?.audio) return;
    cur.audio.enabled = !cur.audio.enabled;
    this.publish({});
    this.broadcastCtl();
  }

  async toggleCamera() {
    const cur = this.cur;
    if (!cur) return;
    if (cur.camera) {
      cur.camera.stop();
      cur.camera = null;
    } else {
      cur.camera = await this.cameraTrack(cur.facing);
    }
    if (!cur.screen) await this.setVideoTrack(cur, cur.camera);
    this.publish({});
    this.broadcastCtl();
  }

  async flipCamera() {
    const cur = this.cur;
    if (!cur?.camera) return;
    cur.facing = cur.facing === "user" ? "environment" : "user";
    const next = await this.cameraTrack(cur.facing);
    cur.camera.stop();
    cur.camera = next;
    if (!cur.screen) await this.setVideoTrack(cur, next);
    this.publish({});
  }

  async toggleScreen() {
    const cur = this.cur;
    if (!cur) return;
    if (cur.screen) return this.stopScreen(cur);
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    const track = stream.getVideoTracks()[0];
    if (!track || this.cur !== cur) return track?.stop();
    cur.screen = track;
    track.onended = () => void this.stopScreen(cur);
    await this.setVideoTrack(cur, track);
    this.publish({});
    this.broadcastCtl();
  }

  /** Engine is shutting down (logout / tab closing). */
  async shutdown() {
    this.orientation.removeEventListener("change", this.onTurn);
    await this.hangup().catch(() => {});
    this.teardown(null);
  }

  // ---- incoming stanzas ------------------------------------------------------------

  /** A 1:1 message carrying a JMI element. Returns true when consumed. */
  onJingleMessage(m: Element, ctx: { archived?: boolean; carbon?: "sent" | "received" }, ts: number): boolean {
    const el = m.children.find((c): c is Element => typeof c !== "string" && c.attrs.xmlns === NS_JMI);
    if (!el) return false;
    if (ctx.archived) return true; // history comes from call logs
    const from = m.attrs.from ?? "";
    const fromBare = bare(from);
    const sid = el.attrs.id;
    const cur = this.cur;
    if (!sid) return true;

    if (ctx.carbon === "sent" || fromBare === this.host.me) {
      // One of our other devices answered or declined: stop ringing here.
      if ((el.name === "proceed" || el.name === "reject") && cur?.sid === sid && call.value?.phase === "incoming")
        this.teardown(el.name === "proceed" ? "Answered on another device" : null);
      return true;
    }

    switch (el.name) {
      case "propose": {
        if (Date.now() - ts > RING_MS || S.blocked.value.has(fromBare)) return true;
        if (cur) {
          if (cur.sid !== sid)
            void this.host.send(
              xml("message", { to: fromBare, type: "chat", id: uuid() }, xml("reject", { xmlns: NS_JMI, id: sid }, xml("reason", {}, xml("busy"))), hint("no-store")),
            );
          return true;
        }
        const media: CallMedia = el.getChildren("description", NS_RTP).some((d) => d.attrs.media === "video") ? "video" : "audio";
        // "Silence unknown callers": someone you haven't saved doesn't ring
        // this device (the caller just hears it ringing); a quiet notice instead.
        if (S.privacy.value.silence_unknown_callers && !S.contacts.value.some((c) => c.jid === fromBare)) {
          if (!this.silenced.has(sid)) {
            this.silenced.add(sid);
            // (The caller logs the missed call when they hang up: it reaches us.)
            window.dispatchEvent(new CustomEvent("xmm-toast", { detail: `Silenced a ${media === "video" ? "video" : "voice"} call from ${S.displayName(fromBare)} (not in your contacts)` }));
          }
          return true;
        }
        const next = this.newCurrent(sid, fromBare, false, media, "in", fromBare);
        next.callerFull = from;
        next.callerDevice = Number(el.getChild("device", NS_CALL)?.attrs.id) || 0;
        this.cur = next;
        this.publish({ phase: "incoming" });
        this.ring(next, ts);
        void this.host.send(xml("message", { to: from, type: "chat", id: uuid() }, xml("ringing", { xmlns: NS_JMI, id: sid }), hint("no-store")));
        return true;
      }
      case "retract":
        if (cur?.sid === sid && cur.chat === fromBare && call.value?.phase === "incoming") this.teardown(null);
        return true;
      case "ringing":
        if (cur?.sid === sid && cur.chat === fromBare && call.value?.phase === "outgoing") this.publish({ phase: "ringing" });
        return true;
      case "proceed": {
        if (cur?.sid !== sid || cur.chat !== fromBare || cur.dir !== "out" || cur.links.size) return true;
        const phase = call.value?.phase;
        if (phase !== "outgoing" && phase !== "ringing") return true;
        clearTimeout(cur.timer);
        this.tones.stop();
        this.publish({ phase: "connecting" });
        const device = Number(el.getChild("device", NS_CALL)?.attrs.id) || 0;
        void this.offer(cur, this.newLink(cur, from, device, true));
        cur.timer = window.setTimeout(() => {
          if (this.cur === cur && !cur.connectedAt) void this.hangup("Couldn't connect");
        }, 30_000);
        return true;
      }
      case "reject":
        if (cur?.sid === sid && cur.chat === fromBare && cur.dir === "out" && !cur.links.size) {
          const busy = !!el.getChild("reason")?.getChild("busy");
          void this.host.logCall(cur.chat, sid, cur.media, busy ? "busy" : "declined", 0);
          this.teardown(busy ? "On another call" : "Declined");
        }
        return true;
      case "finish":
        if (cur?.sid === sid && cur.links.has(from)) {
          const duration = cur.connectedAt ? Math.round((Date.now() - cur.connectedAt) / 1000) : 0;
          if (cur.dir === "out") void this.host.logCall(cur.chat, sid, cur.media, cur.connectedAt ? "answered" : "failed", duration);
          this.teardown("Call ended");
        }
        return true;
    }
    return true;
  }

  /** Encrypted SDP for a call, sent to this device only. */
  async onSignal(fromFull: string, el: Element) {
    const cur = this.cur;
    if (!cur) return;
    let p: { op?: string; sid?: string; kind?: string; sdp?: string };
    try {
      p = (await this.host.decrypt(bare(fromFull), el)) as typeof p;
    } catch (e) {
      console.warn("call signal", (e as Error).message);
      return;
    }
    if (p?.op !== "call" || p.sid !== cur.sid) return;
    const cands = (p as { cands?: unknown }).cands;
    if (p.kind === "cand" ? !Array.isArray(cands) || cands.length > 50 : typeof p.sdp !== "string" || p.sdp.length > 100_000) return;
    // Decryption had to happen in order (ratchet); the rest must not hold up
    // the engine's message queue while ICE candidates are gathered.
    void this.applySignal(cur, fromFull, p as SignalPayload);
  }

  private async applySignal(cur: Current, fromFull: string, p: SignalPayload) {
    if (this.cur !== cur) return;
    let link = cur.links.get(fromFull);
    if (!link && cur.group && cur.joined && p.kind === "offer") {
      // Someone already in the group call connecting to us.
      const g = groupCalls.value.get(cur.chat);
      const who = bare(fromFull);
      if (!S.groups.value.get(cur.chat)?.members.some((x) => x.jid === who)) return;
      const dev = g?.members.get(fromFull)?.device ?? (Number(p.device) || 0);
      link = this.newLink(cur, fromFull, dev, false);
    }
    if (!link) return;
    if (p.kind === "cand") {
      const list = (p.cands ?? []).filter((c) => typeof c?.candidate === "string" && c.candidate.length < 1000);
      if (!link.pc.remoteDescription) link.inCands.push(...list);
      else for (const c of list) await link.pc.addIceCandidate(c).catch(() => {});
      return;
    }
    try {
      await (link as Link & { ready: Promise<void> }).ready; // TURN servers configured
      if (p.kind === "offer") {
        await link.pc.setRemoteDescription({ type: "offer", sdp: p.sdp! });
        await this.drainCands(link);
        for (const t of link.pc.getTransceivers()) {
          const kind = t.receiver.track.kind;
          t.direction = "sendrecv";
          await t.sender.replaceTrack(kind === "audio" ? cur.audio : (cur.screen ?? cur.camera));
        }
        await link.pc.setLocalDescription(await link.pc.createAnswer());
        await this.sendSdp(cur, link, "answer");
      } else if (p.kind === "answer" && link.pc.signalingState === "have-local-offer") {
        await link.pc.setRemoteDescription({ type: "answer", sdp: p.sdp! });
        await this.drainCands(link);
      }
    } catch (e) {
      console.warn("call sdp", e);
    }
  }

  /** Group call control messages. `from` is the member's bare JID. */
  onGroupControl(room: string, from: string, el: Element, ctx: { archived?: boolean }, ts: number) {
    const sid = el.attrs.id;
    if (!sid || ctx.archived) return;
    const g = groupCalls.value.get(room);
    const full = from + "/" + (el.attrs.resource ?? "");
    const device = Number(el.attrs.device) || 0;
    const cur = this.cur;
    switch (el.name) {
      case "call-invite": {
        if (Date.now() - ts > RING_MS * 4) return;
        this.trackGroup(room, { sid, media: el.attrs.media === "video" ? "video" : "audio", startedBy: from, since: ts, members: new Map() }, full, device);
        if (from === this.host.me || cur || Date.now() - ts > RING_MS || S.isMuted(room)) return;
        const next = this.newCurrent(sid, room, true, el.attrs.media === "video" ? "video" : "audio", "in", from);
        this.cur = next;
        this.publish({ phase: "incoming" });
        this.ring(next, ts);
        return;
      }
      case "call-join": {
        if (full === this.host.full) return;
        const info = g?.sid === sid ? g : { sid, media: "audio" as CallMedia, startedBy: from, since: ts, members: new Map() };
        this.trackGroup(room, info, full, device);
        if (cur?.sid === sid && cur.joined && !cur.links.has(full)) {
          // We were here first: we call the newcomer.
          clearTimeout(cur.timer);
          this.tones.stop();
          void this.offer(cur, this.newLink(cur, full, device, true));
        }
        return;
      }
      case "call-leave": {
        if (g?.sid === sid) {
          g.members.delete(full);
          groupCalls.value = new Map(groupCalls.value);
        }
        const link = cur?.sid === sid ? cur.links.get(full) : undefined;
        if (link && cur) this.dropLink(cur, link);
        return;
      }
      case "call-end":
        if (g?.sid === sid) {
          const next = new Map(groupCalls.value);
          next.delete(room);
          groupCalls.value = next;
        }
        if (cur?.sid === sid && call.value?.phase === "incoming") this.teardown(null);
        return;
    }
  }

  async joinGroup(room: string, sid: string) {
    if (this.cur) throw new Error("You're already in a call");
    const g = groupCalls.value.get(room);
    if (g && g.members.size >= MAX_GROUP) throw new Error(`This call is full (${MAX_GROUP} people)`);
    const cur = this.newCurrent(sid, room, true, g?.media ?? "audio", "in", g?.startedBy ?? room);
    this.cur = cur;
    this.publish({ phase: "connecting" });
    try {
      await this.acquire(cur, cur.media === "video");
    } catch (e) {
      this.teardown(null);
      throw e;
    }
    if (this.cur === cur) await this.enterGroup(cur);
  }

  // ---- internals -------------------------------------------------------------------

  private newCurrent(sid: string, chat: string, group: boolean, media: CallMedia, dir: "in" | "out", caller: string): Current {
    return { sid, chat, group, media, dir, caller, links: new Map(), audio: null, camera: null, screen: null, facing: "user", joined: false };
  }

  private resource() {
    return this.host.full.slice(this.host.full.indexOf("/") + 1);
  }

  private async enterGroup(cur: Current) {
    cur.joined = true;
    await this.host.send(
      xml(
        "message",
        { to: cur.chat, type: "groupchat", id: uuid() },
        xml("call-join", { xmlns: NS_CALL, id: cur.sid, device: String(this.host.deviceId()), resource: this.resource() }),
        hint("no-store"),
      ),
    );
    // Members already in the call connect to us; if nobody does, it's over.
    cur.timer = window.setTimeout(() => {
      if (this.cur === cur && !cur.links.size) void this.hangup("The call has ended");
    }, 15_000);
  }

  private async leaveGroup(cur: Current, duration: number) {
    await this.host.send(xml("message", { to: cur.chat, type: "groupchat", id: uuid() }, xml("call-leave", { xmlns: NS_CALL, id: cur.sid, resource: this.resource() }), hint("no-store")));
    const g = groupCalls.value.get(cur.chat);
    const others = g ? [...g.members.keys()].filter((k) => k !== this.host.full).length : 0;
    if (!others) {
      // Last one out records the end on the invite message.
      await this.host.send(
        xml("message", { to: cur.chat, type: "groupchat", id: uuid() }, xml("call-end", { xmlns: NS_CALL, id: cur.sid, duration: String(duration) }), hint("store")),
      );
      const next = new Map(groupCalls.value);
      next.delete(cur.chat);
      groupCalls.value = next;
    } else if (g) {
      g.members.delete(this.host.full);
      groupCalls.value = new Map(groupCalls.value);
    }
  }

  private trackGroup(room: string, info: GroupCallInfo, full: string, device: number) {
    const cur = groupCalls.value.get(room);
    const g = cur?.sid === info.sid ? cur : info;
    g.members.set(full, { full, device });
    const next = new Map(groupCalls.value);
    next.set(room, g);
    groupCalls.value = next;
  }

  private ring(cur: Current, ts: number) {
    this.tones.ring();
    const left = RING_MS - (Date.now() - ts);
    cur.timer = window.setTimeout(() => {
      if (this.cur === cur && call.value?.phase === "incoming") this.teardown(null);
    }, Math.max(1000, left));
    void notifyIncoming(cur);
  }

  private async acquire(cur: Current, video: boolean) {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: video ? cameraConstraints(cur.facing) : false,
      });
    } catch (e) {
      const name = (e as DOMException).name;
      if (name === "NotAllowedError") throw new Error(video ? "Allow camera and microphone access to make video calls" : "Allow microphone access to make calls");
      if (name === "NotFoundError") throw new Error(video ? "No camera or microphone found" : "No microphone found");
      throw e;
    }
    if (this.cur !== cur) return stream.getTracks().forEach((t) => t.stop());
    cur.audio = stream.getAudioTracks()[0] ?? null;
    cur.camera = stream.getVideoTracks()[0] ?? null;
  }

  private async cameraTrack(facing: "user" | "environment") {
    const s = await navigator.mediaDevices.getUserMedia({ video: cameraConstraints(facing) });
    return s.getVideoTracks()[0];
  }

  private async setVideoTrack(cur: Current, track: MediaStreamTrack | null) {
    for (const l of cur.links.values()) {
      const t = l.pc.getTransceivers().find((x) => x.receiver.track.kind === "video");
      await t?.sender.replaceTrack(track).catch(() => {});
    }
  }

  private async stopScreen(cur: Current) {
    if (!cur.screen) return;
    cur.screen.onended = null;
    cur.screen.stop();
    cur.screen = null;
    await this.setVideoTrack(cur, cur.camera);
    this.publish({});
    this.broadcastCtl();
  }

  private async iceConfig(): Promise<RTCConfiguration> {
    if (!this.ice || this.ice.until < Date.now()) {
      try {
        const res = await this.host.iq(xml("iq", { type: "get", to: this.host.me.split("@")[1] }, xml("services", { xmlns: NS_EXTDISCO })));
        const list = res.getChild("services", NS_EXTDISCO)?.getChildren("service") ?? [];
        let until = Date.now() + 3600_000;
        const servers: RTCIceServer[] = [];
        for (const s of list) {
          const { type, host, port, transport, username, password, expires } = s.attrs;
          if ((type !== "stun" && type !== "turn" && type !== "turns") || !host || !port) continue;
          const url = `${type}:${host.includes(":") ? `[${host}]` : host}:${port}` + (type === "stun" ? "" : `?transport=${transport ?? "udp"}`);
          servers.push(username ? { urls: url, username, credential: password } : { urls: url });
          if (expires) until = Math.min(until, Date.parse(expires) - 60_000);
        }
        this.ice = { servers, until };
      } catch (e) {
        console.warn("extdisco", e);
        this.ice = { servers: [], until: Date.now() + 60_000 };
      }
    }
    let relay = false;
    try {
      relay = localStorage.getItem("xmm.forceRelay") === "1"; // debugging: TURN only
    } catch {
      /* storage blocked */
    }
    return { iceServers: this.ice.servers, iceTransportPolicy: relay ? "relay" : "all" };
  }

  private newLink(cur: Current, key: string, device: number, offerer: boolean): Link {
    const pc = new RTCPeerConnection();
    // Configured asynchronously (XEP-0215 lookup); setConfiguration before
    // the first offer/answer is fine.
    const ready = this.iceConfig().then((cfg) => pc.setConfiguration(cfg));
    const stream = new MediaStream();
    const ctl = pc.createDataChannel("ctl", { negotiated: true, id: 0 });
    const view: PeerView = { key, jid: bare(key), stream, state: "connecting", muted: false, video: cur.media === "video", screen: false };
    const link: Link = { key, jid: bare(key), device, pc, ctl, view, offerer, restarts: 0, sdpSent: false, outCands: [], inCands: [], chain: Promise.resolve() };
    pc.onicecandidate = (e) => {
      if (!e.candidate?.candidate) return;
      link.outCands.push(e.candidate.toJSON());
      this.scheduleCands(cur, link);
    };
    (link as Link & { ready: Promise<void> }).ready = ready;
    if (offerer) {
      pc.addTransceiver(cur.audio ?? "audio", { direction: "sendrecv" });
      pc.addTransceiver(cur.screen ?? cur.camera ?? "video", { direction: "sendrecv" });
    }
    pc.ontrack = (e) => {
      if (!stream.getTracks().includes(e.track)) stream.addTrack(e.track);
      this.publish({});
    };
    pc.onconnectionstatechange = () => this.onConnState(cur, link);
    ctl.onopen = () => this.sendCtl(cur, link);
    ctl.onmessage = (e) => {
      try {
        const m = JSON.parse(String(e.data)) as { muted?: boolean; video?: boolean; screen?: boolean };
        view.muted = !!m.muted;
        view.video = !!m.video;
        view.screen = !!m.screen;
        this.publish({});
      } catch {
        /* ignore */
      }
    };
    cur.links.set(key, link);
    this.publish({});
    return link;
  }

  private async offer(cur: Current, link: Link, restart = false) {
    try {
      await (link as Link & { ready: Promise<void> }).ready;
      if (restart) link.pc.restartIce();
      link.sdpSent = false;
      await link.pc.setLocalDescription(await link.pc.createOffer());
      if (this.cur !== cur) return;
      await this.sendSdp(cur, link, "offer");
    } catch (e) {
      console.warn("call offer", e);
      if (!restart) this.dropLink(cur, link, "Couldn't connect");
    }
  }

  /** Send our offer/answer with the candidates gathered so far (host ones
   * are near-instant); the rest follow as "cand" batches (trickle ICE). */
  private async sendSdp(cur: Current, link: Link, kind: "offer" | "answer") {
    await gathered(link.pc, 600);
    if (this.cur !== cur) return;
    const sdp = link.pc.localDescription!.sdp;
    link.outCands = []; // already inside the SDP
    link.sdpSent = true;
    const sent = this.signal(cur, link, kind === "offer" ? { kind, sdp, device: this.host.deviceId() } : { kind, sdp });
    this.scheduleCands(cur, link);
    await sent;
  }

  private scheduleCands(cur: Current, link: Link) {
    if (link.flush || !link.sdpSent || !link.outCands.length) return;
    link.flush = window.setTimeout(() => {
      link.flush = undefined;
      const cands = link.outCands.splice(0, 50);
      if (cands.length && this.cur === cur) void this.signal(cur, link, { kind: "cand", cands });
      this.scheduleCands(cur, link);
    }, 150);
  }

  private async drainCands(link: Link) {
    for (const c of link.inCands.splice(0)) await link.pc.addIceCandidate(c).catch(() => {});
  }

  private signal(cur: Current, link: Link, payload: object) {
    link.chain = link.chain
      .then(async () => {
        const enc = await this.host.encryptFor(link.jid, link.device, { op: "call", sid: cur.sid, ...payload });
        await this.host.send(
          xml("message", { to: link.key, type: "chat", id: uuid() }, enc, hint("no-store"), hint("no-copy"), xml("private", { xmlns: NS_CARBONS })),
        );
      })
      .catch((e) => console.warn("call signal send", e));
    return link.chain;
  }

  private onConnState(cur: Current, link: Link) {
    if (this.cur !== cur) return;
    const st = link.pc.connectionState;
    clearTimeout(link.lost);
    if (st === "connected") {
      link.view.state = "connected";
      link.restarts = 0;
      if (!cur.connectedAt) {
        cur.connectedAt = Date.now();
        clearTimeout(cur.timer);
        this.tones.stop();
      }
      this.publish({ phase: "active", startedAt: call.value?.startedAt ?? cur.connectedAt });
    } else if (st === "disconnected" || st === "failed") {
      link.view.state = "reconnecting";
      this.publish({});
      // The offerer restarts ICE (new candidates, maybe via TURN); give up
      // if the connection doesn't come back.
      if (link.offerer && link.restarts < 2 && (st === "failed" || link.view.state === "reconnecting")) {
        link.lost = window.setTimeout(
          () => {
            if (link.pc.connectionState === "connected") return;
            link.restarts++;
            void this.offer(cur, link, true);
          },
          st === "failed" ? 0 : 3000,
        );
      }
      window.setTimeout(() => {
        if (this.cur === cur && cur.links.get(link.key) === link && link.pc.connectionState !== "connected") this.dropLink(cur, link, "Connection lost");
      }, 20_000);
    }
  }

  private dropLink(cur: Current, link: Link, reason = "Call ended") {
    link.pc.close();
    cur.links.delete(link.key);
    this.publish({});
    if (!cur.links.size && cur.connectedAt) {
      // 1:1: the other side is gone. Group: everyone else left.
      if (cur.group) void this.hangup(cur.group ? "Everyone left" : reason);
      else {
        if (cur.dir === "out") void this.host.logCall(cur.chat, cur.sid, cur.media, "answered", Math.round((Date.now() - cur.connectedAt) / 1000));
        this.teardown(reason);
      }
    } else if (!cur.group && !cur.links.size) {
      if (cur.dir === "out") void this.host.logCall(cur.chat, cur.sid, cur.media, "failed", 0);
      this.teardown(reason);
    }
  }

  private ctlState(cur: Current) {
    return JSON.stringify({ muted: !!cur.audio && !cur.audio.enabled, video: !!(cur.camera || cur.screen), screen: !!cur.screen });
  }

  private sendCtl(cur: Current, link: Link) {
    if (link.ctl.readyState === "open") link.ctl.send(this.ctlState(cur));
  }

  private broadcastCtl() {
    const cur = this.cur;
    if (cur) for (const l of cur.links.values()) this.sendCtl(cur, l);
  }

  private teardown(reason: string | null) {
    const cur = this.cur;
    if (!cur) return;
    cur.done = true;
    clearTimeout(cur.timer);
    this.tones.stop();
    for (const l of cur.links.values()) {
      clearTimeout(l.lost);
      clearTimeout(l.flush);
      l.pc.close();
    }
    cur.links.clear();
    for (const t of [cur.audio, cur.camera, cur.screen]) t?.stop();
    this.cur = null;
    void closeIncomingNotification();
    if (!reason) {
      call.value = null;
      return;
    }
    // Show why it ended for a moment.
    const v = call.value;
    if (v) call.value = { ...v, phase: "ended", ended: reason, local: null, peers: [] };
    this.tones.hangup();
    window.setTimeout(() => {
      if (call.value?.sid === cur.sid && call.value.phase === "ended") call.value = null;
    }, 1800);
  }

  /** Rebuild the view from the current state. */
  private publish(patch: Partial<CallView>) {
    const cur = this.cur;
    if (!cur) return;
    const prev = call.value?.sid === cur.sid ? call.value : null;
    const video = cur.screen ?? cur.camera;
    const local = prev?.local && sameTracks(prev.local, [cur.audio, video]) ? prev.local : new MediaStream([cur.audio, video].filter((t): t is MediaStreamTrack => !!t));
    call.value = {
      sid: cur.sid,
      chat: cur.chat,
      group: cur.group,
      media: cur.media,
      dir: cur.dir,
      caller: cur.caller,
      phase: prev?.phase ?? "outgoing",
      startedAt: prev?.startedAt,
      ...patch,
      local,
      muted: !!cur.audio && !cur.audio.enabled,
      camera: !!cur.camera,
      screen: !!cur.screen,
      canFlip: !!cur.camera && hasManyCameras,
      peers: [...cur.links.values()].map((l) => ({ ...l.view })),
    };
  }
}

function sameTracks(s: MediaStream, tracks: (MediaStreamTrack | null)[]) {
  const want = tracks.filter(Boolean);
  const have = s.getTracks();
  return want.length === have.length && want.every((t) => have.includes(t!));
}

let hasManyCameras = false;
if (typeof navigator !== "undefined" && navigator.mediaDevices?.enumerateDevices)
  void navigator.mediaDevices
    .enumerateDevices()
    .then((d) => (hasManyCameras = d.filter((x) => x.kind === "videoinput").length > 1))
    .catch(() => {});

/** Wait for ICE gathering (we don't trickle candidates), at most `ms`. */
function gathered(pc: RTCPeerConnection, ms = 2500): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms);
    pc.addEventListener("icegatheringstatechange", () => {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(t);
        resolve();
      }
    });
  });
}

async function notifyIncoming(cur: Current) {
  if (document.visibilityState === "visible" && document.hasFocus()) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const reg = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
  const title = cur.group ? `${S.displayName(cur.caller)} @ ${S.displayName(cur.chat)}` : S.displayName(cur.caller);
  const body = cur.media === "video" ? "Incoming video call" : "Incoming voice call";
  const opts: NotificationOptions = { body, tag: "xmm-call", requireInteraction: true, data: { chat: cur.chat }, icon: "/icon.svg" };
  if (reg) await reg.showNotification(title, opts).catch(() => {});
  else new Notification(title, opts);
}

async function closeIncomingNotification() {
  const reg = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
  for (const n of (await reg?.getNotifications({ tag: "xmm-call" }).catch(() => [])) ?? []) n.close();
}

// ---- tones -------------------------------------------------------------------------

/** Ring / ringback / hang-up tones, synthesized (no audio files to fetch). */
class Tones {
  private ctx: AudioContext | null = null;
  private timer = 0;
  private nodes: OscillatorNode[] = [];

  private beep(freqs: number[], ms: number, gain = 0.08) {
    try {
      this.ctx ??= new AudioContext();
      void this.ctx.resume();
      const g = this.ctx.createGain();
      g.gain.value = gain;
      g.connect(this.ctx.destination);
      const t = this.ctx.currentTime;
      for (const f of freqs) {
        const o = this.ctx.createOscillator();
        o.frequency.value = f;
        o.connect(g);
        o.start(t);
        o.stop(t + ms / 1000);
        this.nodes.push(o);
        o.onended = () => (this.nodes = this.nodes.filter((x) => x !== o));
      }
    } catch {
      /* no audio */
    }
  }

  private loop(fn: () => void, every: number) {
    this.stop();
    fn();
    this.timer = window.setInterval(fn, every);
  }

  /** Incoming call. */
  ring() {
    this.loop(() => {
      this.beep([880, 1320], 400, 0.06);
      window.setTimeout(() => this.beep([880, 1320], 400, 0.06), 600);
    }, 3000);
  }

  /** Outgoing call, waiting for an answer. */
  ringback() {
    this.loop(() => this.beep([425], 1000, 0.05), 4000);
  }

  hangup() {
    this.stop();
    this.beep([480, 620], 250, 0.05);
  }

  stop() {
    clearInterval(this.timer);
    for (const o of this.nodes) {
      try {
        o.stop();
      } catch {
        /* already stopped */
      }
    }
    this.nodes = [];
  }
}
